import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Trick from "../../components/Trick";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import SocialMediaIcons from "../../components/SocialMediaIcons";
import { TrickData } from "../../services/trickService";
import { ApiService, FormulaData } from "../../services/apiService";
import { getUserProfile } from "../../utilities/axios.utilities";

const CongThuc50: React.FC = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTrick, setShowTrick] = useState(false);
  const [selectedFormula, setSelectedFormula] = useState<string | number | null>(null);
  const [formulas, setFormulas] = useState<FormulaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrick, setSelectedTrick] = useState<TrickData | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [userCoins, setUserCoins] = useState<number>(0);
  const [showInsufficientCoinsModal, setShowInsufficientCoinsModal] = useState(false);
  const [showUpdatingModal, setShowUpdatingModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  // Check mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load tricks and user coins from localStorage (same as header)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const formulasData = await ApiService.getAllFormulas();
        setFormulas(formulasData);
        
        // Get coins from localStorage (same as header)
        const userInfoString = localStorage.getItem("user_info");
        if (userInfoString) {
          const userInfo = JSON.parse(userInfoString);
          const coins = Number(userInfo.coins) || 0;
          setUserCoins(coins);
        } else {
          // Fallback to API if no localStorage
          const userProfile = await getUserProfile();
          const coins = Number(userProfile.coins) || 0;
          setUserCoins(coins);
        }
      } catch (error) {
        setFormulas([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleCardClick = (formulaNumber: string | number) => {
    setSelectedFormula(formulaNumber);
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (!selectedFormula) return;

    try {
      const coinsRequired = getCoinsRequired(selectedFormula);
      
      // Check if user has enough coins (for both free and paid formulas)
      if (userCoins < (coinsRequired || 0)) {
        setShowConfirmModal(false);
        setShowInsufficientCoinsModal(true);
        return;
      }

      // Get formula data from API
      // Try multiple matching strategies
      const formulaData = formulas.find(f => 
        f._id === selectedFormula || 
        f.id === selectedFormula || 
        f.title === selectedFormula.toString() ||
        f.order === selectedFormula
      );
      
      if (!formulaData) {
        // Formula doesn't exist yet - show updating message
        setShowConfirmModal(false);
        setShowTrick(false);
        setShowInsufficientCoinsModal(false);
        setShowUpdatingModal(true);
        return;
      }

      // Convert FormulaData to TrickData format
      const trickData: TrickData = {
        id: formulaData.id || parseInt(formulaData._id || '0'),
        type: formulaData.type,
        title: formulaData.title,
        shortDescription: formulaData.description,
        order: formulaData.order || 1, // Default to 1 if undefined
        coinsRequired: formulaData.coinsRequired || 0, // Default to 0 if undefined
        image: formulaData.imageUrl || getMockImagePath(selectedFormula), // Use thumbnail image
        contentImageUrl: formulaData.contentImageUrl, // Use content image
        content: formulaData.content || 'Nội dung đang được cập nhật...',
        createdAt: formulaData.createdAt,
        updatedAt: formulaData.updatedAt
      };

      // Deduct coins for all formulas (free and paid)
      if ((coinsRequired || 0) > 0) {
        // Use API to deduct coins
        const result = await ApiService.deductCoins(coinsRequired || 0);
        
        if (!result) {
          setShowConfirmModal(false);
          setShowInsufficientCoinsModal(true);
          return;
        }
        
        // Update coins state (deduct from current balance)
        const newCoins = userCoins - (coinsRequired || 0);
        setUserCoins(newCoins);
        
        // Update localStorage to sync with header
        const userInfoString = localStorage.getItem("user_info");
        if (userInfoString) {
          const userInfo = JSON.parse(userInfoString);
          userInfo.coins = newCoins;
          localStorage.setItem("user_info", JSON.stringify(userInfo));
        }
      }
      
      // Load trick data
      setSelectedTrick(trickData);
      setShowConfirmModal(false);
      setShowTrick(true);
    } catch (error: any) {
      alert('Có lỗi xảy ra! Vui lòng thử lại sau.');
    }
  };

  // Calculate coins required based on formula number
  const getCoinsRequired = (formulaNumber: string | number) => {
    const formula = formulas.find(f => 
      f._id === formulaNumber.toString() || 
      f.id === formulaNumber || 
      f.title === formulaNumber.toString() ||
      f.order === formulaNumber
    );
    
    return formula ? formula.coinsRequired : 0;
  };

  // Get display text for coins
  const getCoinsText = (formulaNumber: string | number) => {
    const coins = getCoinsRequired(formulaNumber) || 0;
    if (coins === 0) return "MIỄN PHÍ";
    if (coins < 100) return `${coins} XU`;
    return `${coins.toLocaleString('vi-VN')} XU`;
  };

  // Get mock image path based on formula number
  const getMockImagePath = (formulaNumber: string | number) => {
    const number = typeof formulaNumber === 'string' ? parseInt(formulaNumber) : formulaNumber;
    
    // Map formula numbers to ct-kieu-trinh images (1-39)
    if (number >= 1 && number <= 39) {
      return `/assets/ct-kieu-trinh/${number}.webp`;
    }
    
    // Fallback to ct1.png for numbers outside 1-39 range
    return "/assets/ct1.png";
  };

  const handleCancel = () => {
    // Xử lý khi user chọn "Không"
    setShowConfirmModal(false);
    setSelectedFormula(null);
  };

  const handleCloseTrick = () => {
    setShowTrick(false);
    setSelectedFormula(null);
    setSelectedTrick(null);
  };


  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(${isMobile ? "/assets/bg_mobile_2.jpg" : "/assets/bg_pc_2.jpg"})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >

      {/* Header */}
      <Header setIsShowLogout={() => setShowLogoutModal(true)} />

      {/* Logo and Back Button - Below Header */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          {/* Logo - positioned above KT01 (first image) - Hidden on mobile */}
          <img 
            src="/assets/logo-2.png" 
            alt="logo" 
            className="h-20 hidden sm:block"
          />
          
          {/* Back Button - positioned above KT04 (fourth image) - Smaller on mobile */}
          <button
            onClick={() => navigate("/")}
            className="group relative flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-full font-bold text-[#DF3125] transition-all duration-500 hover:scale-110 hover:rotate-1 overflow-hidden
                       text-sm sm:text-lg"
            style={{
              background: 'linear-gradient(135deg, #F9D332 0%, #FFD700 50%, #F9D332 100%)',
              border: '3px solid #FFA500',
              boxShadow: '0 8px 25px rgba(249, 211, 50, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.3), inset 0 -2px 0 rgba(0, 0, 0, 0.1)',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              animation: 'pulse-glow 2s ease-in-out infinite alternate'
            }}
          >
            {/* Animated background shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            {/* Left arrows with animation */}
            <div className="flex group-hover:animate-bounce">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300 -ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
            </div>
            
            <span className="font-extrabold group-hover:tracking-wider transition-all duration-300">Quay lại</span>
            
            {/* Right arrows with animation */}
            <div className="flex group-hover:animate-bounce">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300 -ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content - Grid of Images */}
      <div className="px-6 pb-20">
        {loading ? (
          <div className="flex justify-center items-center min-h-96">
            <div className="text-white text-xl">Đang tải...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {/* Display 39 images like before */}
            {Array.from({ length: 39 }, (_, index) => {
              const formulaNumber = index + 1;
              
              return (
                <div
                  key={formulaNumber}
                  onClick={() => handleCardClick(formulaNumber)}
                  className="cursor-pointer hover:scale-105 transition-transform"
                >
                  <img
                    src={getMockImagePath(formulaNumber)}
                    alt={`Công thức ${formulaNumber}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Social Media Icons */}
      <SocialMediaIcons />

      {/* Confirmation Modal - Clean Design */}
      {showConfirmModal && selectedFormula && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Background overlay */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          
          {/* Modal Container */}
          <div className="relative bg-gradient-to-br from-slate-800/90 via-slate-700/85 to-slate-800/90 backdrop-blur-md rounded-2xl p-6 max-w-2xl mx-auto shadow-2xl border border-slate-600/20 overflow-hidden">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-3">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, #fbbf24 0%, transparent 40%), 
                                radial-gradient(circle at 75% 75%, #10b981 0%, transparent 40%)`,
              }}></div>
            </div>
            
            {/* Elegant top accent */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"></div>
            
            {/* Question Mark Icon - Top Center */}
            <div className="flex justify-center mb-6 relative z-10">
              <div className="relative">
                {/* Gentle glow effect */}
                <div className="absolute inset-0 w-20 h-20 bg-cyan-400/8 rounded-full blur-md"></div>
                {/* Main icon */}
                <div className="relative w-20 h-20 bg-gradient-to-br from-slate-600 via-slate-500 to-slate-700 rounded-full flex items-center justify-center shadow-xl border-2 border-slate-400/20">
                  <span className="text-4xl font-light text-white">?</span>
                </div>
                {/* Inner highlight */}
                <div className="absolute inset-2 bg-gradient-to-br from-white/15 to-transparent rounded-full"></div>
              </div>
            </div>
            
            {/* Formula Number Badge - Center */}
            <div className="flex justify-center mb-6 relative z-10">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400/15 to-orange-500/15 blur-md rounded-full"></div>
                <div className="relative bg-gradient-to-r from-amber-400 to-orange-500 rounded-full px-8 py-3 shadow-lg">
                  <span className="text-lg font-bold text-slate-900 tracking-wide">CÔNG THỨC SỐ {selectedFormula}</span>
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-light text-white text-center mb-6 relative z-10 tracking-wider">
              <span className="bg-gradient-to-r from-amber-200 via-white to-amber-200 bg-clip-text text-transparent">
                XÁC NHẬN
              </span>
            </h2>

            {/* Cost Display - Horizontal Layout */}
            <div className="text-center mb-8 relative z-10">
              <div className="bg-gradient-to-r from-slate-700/40 to-slate-600/40 backdrop-blur-sm rounded-xl p-4 border border-slate-500/20 shadow-inner">
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mr-3 shadow-md">
                    <span className="text-lg">🪙</span>
                  </div>
                  <p className="text-xl font-medium text-emerald-300 tracking-wide">
                    {getCoinsText(selectedFormula)}
                  </p>
                </div>
                <div className="w-12 h-0.5 bg-gradient-to-r from-amber-400 via-emerald-400 to-cyan-400 mx-auto rounded-full mt-2"></div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-8 relative z-10">
              <button
                onClick={handleConfirm}
                className="flex-1 rounded-xl font-medium py-3 px-8 text-white transition-colors duration-200 hover:text-yellow-300"
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065F46 100%)',
                  boxShadow: '0 8px 25px rgba(5, 150, 105, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                  border: '1px solid #10b981/30'
                }}
              >
                CÓ
              </button>
              
              <button
                onClick={handleCancel}
                className="flex-1 rounded-xl font-medium py-3 px-8 text-white transition-colors duration-200 hover:text-yellow-300"
                style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)',
                  boxShadow: '0 8px 25px rgba(220, 38, 38, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                  border: '1px solid #ef4444/30'
                }}
              >
                KHÔNG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Coins Modal */}
      {showInsufficientCoinsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Background overlay */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          
          {/* Modal Container */}
          <div className="relative bg-gradient-to-br from-slate-800/90 via-slate-700/85 to-slate-800/90 backdrop-blur-md rounded-2xl p-8 max-w-md mx-auto shadow-2xl border border-slate-600/20 overflow-hidden">
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-xl">
                <span className="text-4xl">⚠️</span>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">
              <span className="bg-gradient-to-r from-red-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                KHÔNG ĐỦ XU
              </span>
            </h2>

            {/* Message */}
            <div className="text-center mb-8">
              <p className="text-lg text-gray-300 mb-4">
                Số xu hiện tại: <span className="text-yellow-400 font-bold">{userCoins} XU</span>
              </p>
              <p className="text-lg text-gray-300 mb-4">
                Cần: <span className="text-red-400 font-bold">{selectedFormula ? getCoinsText(selectedFormula) : '0 XU'}</span>
              </p>
              <p className="text-white font-medium">
                Vui lòng liên hệ admin để nạp thêm xu!
              </p>
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              <button
                onClick={() => setShowInsufficientCoinsModal(false)}
                className="px-8 py-3 rounded-xl font-medium text-white transition-colors duration-200 hover:text-yellow-300"
                style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)',
                  boxShadow: '0 8px 25px rgba(220, 38, 38, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                  border: '1px solid #ef4444/30'
                }}
              >
                ĐÓNG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Updating Modal */}
      {showUpdatingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Background overlay */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          
          {/* Modal content */}
          <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-gray-700/50">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">
              🚀 ĐANG CẬP NHẬT
            </h2>
            
            {/* Description */}
            <p className="text-gray-300 text-center mb-6 leading-relaxed">
              Công thức <span className="text-yellow-400 font-semibold">CT{selectedFormula}</span> đang được đội ngũ chuyên gia phát triển và hoàn thiện.
            </p>
            
            <p className="text-gray-400 text-sm text-center mb-6">
              Chúng tôi đang nỗ lực để mang đến cho bạn những phương pháp chơi tối ưu nhất!
            </p>

            {/* Contact info */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-4 mb-6 border border-yellow-500/20">
              <p className="text-yellow-300 text-center font-medium mb-2">
                📞 Liên hệ Admin để được hỗ trợ
              </p>
              <p className="text-white text-center text-sm">
                Telegram: <span className="text-yellow-400 font-semibold">@KieuTrinhCT</span>
              </p>
            </div>

            {/* Close button */}
            <div className="flex justify-center">
              <button
                onClick={() => setShowUpdatingModal(false)}
                className="px-8 py-3 rounded-xl font-medium text-white transition-colors duration-200 hover:text-yellow-300"
                style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)',
                  boxShadow: '0 8px 25px rgba(220, 38, 38, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                  border: '1px solid #ef4444/30'
                }}
              >
                ĐÓNG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trick Component */}
      {showTrick && selectedTrick && (
        <Trick 
          data={selectedTrick} 
          onClose={handleCloseTrick} 
        />
      )}

      {/* Logout Modal */}
      <ModalConfirmLogout 
        isShowLogout={showLogoutModal}
        setIsShowLogout={() => setShowLogoutModal(false)}
      />
    </div>
  );
};

export default CongThuc50;